/**
 * TaskPanelBell — floating bell for office-staff (KUMKUM, DHARMENDRA YADAV)
 * and admins. Surfaces TWO catalog hygiene queues in one badge:
 *   1. Parts pending photo
 *   2. Parts pending UOM + weight
 *
 * Click → opens the combined `/staff/task-panel` hub which renders both
 * queues as side-by-side tabs.
 *
 * 2026-06-17 — replaces the older `MissingImagesBell` (one queue only).
 *               Same position bottom-[17rem] so it doesn't overlap the
 *               StockTrailBell + TallyPendingBell stack.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_MOBILES = new Set(["9999313175", "9811117751", "9643558419"]);
const OFFICE_STAFF_MOBILES = new Set(["8287369318", "8052250605"]); // Kumkum, Dharmendra

const TaskPanelBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [photoCount, setPhotoCount] = useState(0);
  const [uomCount, setUomCount] = useState(0);

  const role = String(user?.role || "").toLowerCase();
  const mobile = String(user?.mobile || "");
  const enabled =
    role === "office_staff" ||
    user?.is_admin === true ||
    ADMIN_MOBILES.has(mobile) ||
    OFFICE_STAFF_MOBILES.has(mobile);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      // Parallel — neither blocks the other; either failure leaves the
      // previous count in place rather than zeroing the badge mid-poll.
      const [photos, uom] = await Promise.allSettled([
        axios.get(`${API}/task-panel/missing-images/count`),
        axios.get(`${API}/task-panel/missing-uom-weight/count`),
      ]);
      if (photos.status === "fulfilled") {
        setPhotoCount(Number(photos.value?.data?.count || 0));
      }
      if (uom.status === "fulfilled") {
        setUomCount(Number(uom.value?.data?.count || 0));
      }
    } catch {
      /* silent — auth/network blips shouldn't surface a toast */
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [enabled, load]);

  if (!enabled) return null;

  const total = photoCount + uomCount;
  const active = total > 0;

  return (
    <div className="fixed bottom-[17rem] right-4 z-[60]">
      <button
        type="button"
        onClick={() => navigate("/staff/task-panel")}
        data-testid="task-panel-bell"
        title={`Catalog pending: ${photoCount} photos · ${uomCount} UOM/weight`}
        aria-label="Catalog hygiene tasks"
        className={`relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
          active
            ? "bg-fuchsia-600 hover:bg-fuchsia-700"
            : "bg-slate-700 hover:bg-slate-800"
        }`}
      >
        <ClipboardList className={`w-5 h-5 text-white ${active ? "animate-pulse" : ""}`} />
        {active && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-white text-fuchsia-700 text-[11px] font-bold flex items-center justify-center px-1"
            data-testid="task-panel-bell-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>
    </div>
  );
};

export default TaskPanelBell;
